import { createContext, useContext, useEffect, useId, useRef, useState, type KeyboardEvent, type ReactNode } from 'react';
import { evaluateFormCondition } from '@ayumia/component-runtime';
import type { QueryEnvelope } from '../api/client';
import { text } from '../app/i18n';
import { FieldInput, inputFieldOf, type InputField } from '../views/FieldInput';
import { useLoad } from '../views/useLoad';
import { useAppServices } from '../views/ViewContext';
import { formatTarget, formatValue, resolveTarget, targetState, unitSymbol, type Answers, type ConformityRules, type Entry, type Inputs, type TargetState } from './form-targets';
import { isCivilDate } from './civil-date';
import { useSuggested } from './form-reactions-context';
import { digitsFromTyping, digitsMatchPattern } from './pad-input';
import { PadDateInput } from './PadDateInput';
import { DateTimeInput } from './widgets/DateTimeInput';
import { RichTextBlock } from './widgets/RichText';
import { Slider } from './widgets/Slider';
import { Stars } from './widgets/Stars';
import { Stepper } from './widgets/Stepper';
import { Toggle } from './widgets/Toggle';
import { LotField } from './widgets/LotField';
import { ScanCode } from './widgets/ScanCode';
import { Icon } from '../design-system/icons';
import { OptionTiles, Segmented } from './form-v2/OptionTiles';
import { EchoName, PersonControl } from './form-v2/PersonField';
import type { Bounds, WidgetProps } from './widgets/types';
import { SELECTOR_LIMIT, selectorCard, selectorCardMatches, selectorClearable, selectorLabel, selectorOf, selectorQuery, selectorTruncated, type ObjectSelectorSpec, type SelectorCard, type SelectorRow } from './object-selector';

/** Délai avant d'interroger le serveur pendant la frappe (liste longue). */
const SELECTOR_SEARCH_DELAY_MS = 250;
import './template-form.css';
import './widgets/widgets.css';

/**
 * Un champ de gabarit 1.2.0 sur le web (ADR-141 §3 bis) : le contrôle choisi par
 * `presentation.widget` (registre widget → composant ; un widget inconnu ou inadapté au type retombe
 * sur le rendu du type), la cible sous le champ, l'état hors cible pendant la saisie, le champ
 * calculé en lecture seule, le sélecteur d'objet d'un champ `reference` et la date civile d'un champ
 * `date`, comme la tablette. Tout cela est de la présentation : le serveur valide, calcule et tranche.
 */

type Widget = NonNullable<NonNullable<Entry['presentation']>['widget']>;

/** Tuiles de choix (énumération courte, oui/non) : un groupe radio, un clic par réponse. */
function RadioTiles({ id, field, value, onChange, disabled, describedBy, dangerOf, optionIcons }: WidgetProps): ReactNode {
  if (field.type !== 'enumeration' && field.type !== 'boolean') return null;
  const options = field.type === 'boolean' ? ['true', 'false'] : [...(field.enum_values ?? [])];
  if (options.length === 0 || options.length > 8) return null;
  const current = value === null || value === undefined ? '' : String(value);
  return (
    <div className="ay-tiles" role="radiogroup" aria-labelledby={`${id}-label`} aria-describedby={describedBy}>
      {options.map((option) => (
        <label key={option} className={`ay-tiles__tile${current === option ? ' ay-tiles__tile--selected' : ''}${dangerOf?.(option) === true ? ' ay-tiles__tile--danger' : ''}`}>
          <input type="radio" name={id} value={option} checked={current === option} disabled={disabled} onChange={() => onChange(field.type === 'boolean' ? option === 'true' : option)} />
          {optionIcons?.[option] !== undefined && (
            <span className="ay-tiles__icon" data-icon={optionIcons[option]}>
              <Icon name={optionIcons[option]} size={22} />
            </span>
          )}
          <span>{field.type === 'boolean' ? (option === 'true' ? 'Oui' : 'Non') : text(field.enum_labels?.[option], 'fr', option)}</span>
        </label>
      ))}
    </div>
  );
}

/** Heure du jour « HH:MM » (sur un champ texte) ; le poste a son propre sélecteur. */
function TimeInput({ id, field, value, onChange, disabled, invalid, describedBy }: WidgetProps): ReactNode {
  if (field.type !== 'string') return null;
  return <input id={id} type="time" required={field.required === true} disabled={disabled} aria-invalid={invalid} aria-describedby={describedBy} value={typeof value === 'string' ? value : ''} onChange={(event) => onChange(event.target.value === '' ? null : event.target.value)} />;
}

/**
 * Date du jour civile du formulaire (au fuseau du site, sinon du tenant), fournie par
 * {@link TemplateForm} ; `null` : aucun fuseau connu, le calendrier n'a pas de bornes.
 */
export const CivilTodayContext = createContext<string | null>(null);

/**
 * Tâche du formulaire (fournie par {@link TemplateForm}) : le sélecteur d'objet la passe à la liste
 * d'objets (`work_item_id`), qui ne propose alors que ce que la commande acceptera. Hors tâche : rien.
 */
export const FormWorkItemContext = createContext<string | undefined>(undefined);

/** Bornes des règles de conformité citées par le formulaire (work-item-resolution 2.4.0, `conformity_rules`). */
export const ConformityRulesContext = createContext<ConformityRules>({});

/** Valeur montrée par la date civile : la chaîne AAAA-MM-JJ telle quelle, sinon rien (aucune conversion). */
export function civilDateValue(value: unknown): string {
  return isCivilDate(value) ? value : '';
}

/**
 * Chaîne déclarée `presentation.widget: keypad` (bague, code) : chiffres seulement, pavé numérique du
 * poste (`inputmode="numeric"`), la valeur reste une chaîne avec ses zéros de tête, dans la limite de
 * `validation.max_length` ; un écart à `validation.pattern` est signalé (le serveur tranche). Comme la
 * tablette, aucun cas particulier sur le nom du champ. Un nombre garde son rendu du type.
 */
function KeypadDigits({ id, field, value, onChange, disabled, invalid, describedBy }: WidgetProps): ReactNode {
  if (field.type !== 'string' && field.type !== 'text') return null;
  const text = typeof value === 'string' ? value : '';
  const mismatch = !digitsMatchPattern(text, field.pattern);
  return (
    <>
      <input
        id={id}
        type="text"
        inputMode="numeric"
        pattern="[0-9]*"
        autoComplete="off"
        spellCheck={false}
        className="ay-digits"
        placeholder="Chiffres seulement"
        required={field.required === true}
        disabled={disabled}
        {...(field.max_length === undefined ? {} : { maxLength: field.max_length })}
        aria-invalid={invalid || mismatch}
        aria-describedby={describedBy}
        value={text}
        onChange={(event) => {
          const next = digitsFromTyping(event.target.value, field.max_length);
          onChange(next === '' ? null : next);
        }}
      />
      {mismatch && <span className="ay-field__error">N’a pas la forme attendue.</span>}
    </>
  );
}

/**
 * Sélecteur d'objet d'un champ `reference` (`presentation.selector`, ADR-141 §1.2) : la liste
 * d'objets du type référencé, lue par la requête gouvernée de l'API avec le filtre déclaré, la
 * recherche sur « code — libellé », l'identifiant choisi comme réponse. Même parcours que la tablette
 * (bouton « Choisir », liste avec recherche) ; au poste, flèches, Entrée et Échap. Liste longue (le
 * serveur a plus d'objets que ceux lus) : la recherche interroge le serveur (`q`, ADR-094). Un champ
 * facultatif s'efface ; un champ obligatoire, jamais.
 */
export function ObjectSelector({ id, type, selector, label, value, onChange, disabled, invalid, describedBy, required, startOpen = false, onClose }: { id: string; type: string; selector: ObjectSelectorSpec; label: string; value: unknown; onChange: (value: unknown) => void; disabled: boolean; invalid: boolean; describedBy: string | undefined; required: boolean; /** Bouton-sélecteur (1.10.0) : la liste s'ouvre d'office, sa fermeture est rendue à l'appelant. */ startOpen?: boolean; onClose?: () => void }) {
  const services = useAppServices();
  const workItemId = useContext(FormWorkItemContext);
  const listId = `${useId()}-options`;
  const query = selectorQuery(selector, undefined, workItemId);
  const filterKey = `${JSON.stringify(query.filter)}:${workItemId ?? ''}`;
  const [rows] = useLoad<QueryEnvelope<SelectorRow[]>>(`selector:${type}:${filterKey}`, () => services.client.listObjects<SelectorRow>(type, query));
  const [open, setOpen] = useState(startOpen);
  const [search, setSearch] = useState('');
  const [active, setActive] = useState(0);
  // Objet choisi dans cette saisie : son libellé reste connu même s'il vient d'une recherche au serveur.
  const [picked, setPicked] = useState<SelectorRow | undefined>(undefined);
  const [found, setFound] = useState<{ q: string; rows?: SelectorRow[]; error?: string } | null>(null);
  const searchable = selector.search !== false;
  const truncated = rows.status === 'ready' && selectorTruncated(rows.value.page);
  const remote = truncated && search.trim() !== '';
  const selectorRef = useRef(selector);
  selectorRef.current = selector;

  useEffect(() => {
    if (!remote || !open) return;
    const q = search.trim();
    let alive = true;
    const timer = setTimeout(() => {
      services.client.listObjects<SelectorRow>(type, selectorQuery(selectorRef.current, q, workItemId)).then(
        (response) => {
          if (alive) setFound({ q, rows: response.data });
        },
        (error: unknown) => {
          if (alive) setFound({ q, error: error instanceof Error ? error.message : String(error) });
        },
      );
    }, SELECTOR_SEARCH_DELAY_MS);
    return () => {
      alive = false;
      clearTimeout(timer);
    };
  }, [remote, open, search, type, filterKey, workItemId, services.client]);

  const current = typeof value === 'string' && value !== '' ? value : undefined;
  const loaded = rows.status === 'ready' ? rows.value.data.filter((row) => row.id !== undefined && row.id !== null) : [];
  const chosen = current === undefined ? undefined : (picked !== undefined && String(picked.id) === current ? picked : loaded.find((row) => String(row.id) === current));
  // Jamais l'identifiant technique à l'écran : un choix pas encore relu s'annonce comme tel.
  const shown = current === undefined ? undefined : chosen === undefined ? (rows.status === 'loading' ? 'Chargement…' : 'Choix enregistré') : selectorLabel(chosen, selector);
  const answered = remote && found !== null && found.q === search.trim() ? found : undefined;
  // Recherche sur le nom, le numéro et la commune parmi les objets lus ; liste longue : ce que le serveur a
  // trouvé (sur ses champs cherchables), puis les objets lus qui répondent par leur numéro ou leur commune.
  const local = loaded.filter((row) => selectorCardMatches(row, selector, search));
  const foundRows = (answered?.rows ?? []).filter((row) => row.id !== undefined && row.id !== null);
  const matches = remote ? [...foundRows, ...local.filter((row) => !foundRows.some((hit) => String(hit.id) === String(row.id)))] : local;
  const highlighted = matches.length === 0 ? undefined : Math.min(active, matches.length - 1);
  const clearable = !disabled && selectorClearable(required, current);

  const close = (): void => {
    setOpen(false);
    setSearch('');
    setActive(0);
    setFound(null);
    onClose?.();
  };
  const choose = (row: SelectorRow): void => {
    setPicked(row);
    onChange(String(row.id));
    close();
  };
  const onKeyDown = (event: KeyboardEvent<HTMLElement>): void => {
    if (event.key === 'ArrowDown' && matches.length > 0) {
      event.preventDefault();
      setActive((index) => (index + 1) % matches.length);
    } else if (event.key === 'ArrowUp' && matches.length > 0) {
      event.preventDefault();
      setActive((index) => (index - 1 + matches.length) % matches.length);
    } else if (event.key === 'Enter') {
      // Entrée choisit l'objet surligné, sans soumettre le formulaire.
      event.preventDefault();
      if (highlighted !== undefined) choose(matches[highlighted]!);
    } else if (event.key === 'Escape') {
      event.preventDefault();
      close();
    }
  };

  if (!open) {
    // Objet choisi et connu : sa carte (avatar, nom, numéro · commune) et son résumé, comme l'encart
    // « Détails du chasseur » du v1 (résumé d'un tiers seulement : adresse, téléphone, courriel) ; la toucher rouvre le choix.
    const card = chosen === undefined ? undefined : selectorCard(chosen, selector);
    const trigger = card !== undefined ? (
      <button
        id={id}
        type="button"
        className="ay-selector__chosen"
        aria-haspopup="listbox"
        aria-expanded={false}
        aria-invalid={invalid}
        aria-describedby={describedBy}
        disabled={disabled}
        onClick={() => setOpen(true)}
      >
        <SelectorCardBody card={card} large />
        {type === 'Party' && (
        <span className="ay-selector__details">
          {card.details.map((detail) => (
            <span key={detail.kind} data-testid={`selector-detail-${detail.kind}`}>
              <span className="ay-selector__detail-label">{DETAIL_LABELS[detail.kind]} : </span>
              {detail.value === null ? <em className="ay-selector__missing">Manquant</em> : <strong>{detail.value}</strong>}
            </span>
          ))}
        </span>
        )}
      </button>
    ) : (
      <button
        id={id}
        type="button"
        className={`ay-selector__trigger${shown === undefined ? ' ay-selector__trigger--empty' : ''}`}
        aria-haspopup="listbox"
        aria-expanded={false}
        aria-invalid={invalid}
        aria-describedby={describedBy}
        disabled={disabled}
        onClick={() => setOpen(true)}
      >
        <svg className="ay-selector__icon" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.9" strokeLinecap="round" aria-hidden="true">
          <path d="M11 18a7 7 0 1 0 0-14 7 7 0 0 0 0 14zM20 20l-4-4" />
        </svg>
        <span className="ay-selector__value">{shown ?? 'Rechercher…'}</span>
        <span className="ay-selector__chevron" aria-hidden="true">
          ▾
        </span>
      </button>
    );
    if (!clearable) return trigger;
    return (
      <span className="ay-selector__row">
        {trigger}
        <button
          type="button"
          className="ay-button ay-button--secondary ay-selector__clear"
          aria-label={`Effacer : ${label}`}
          onClick={() => {
            setPicked(undefined);
            onChange(null);
          }}
        >
          Effacer
        </button>
      </span>
    );
  }
  const optionId = (index: number): string => `${listId}-${index}`;
  return (
    <div className="ay-selector">
      {searchable && (
        <input
          id={id}
          type="search"
          role="combobox"
          className="ay-selector__search"
          placeholder="Nom, numéro, commune"
          autoComplete="off"
          // Jamais le clavier virtuel ouvert d'office (écran tactile) ; au poste, la recherche prend le focus.
          autoFocus={typeof window !== 'undefined' && typeof window.matchMedia === 'function' && window.matchMedia('(pointer: fine)').matches}
          aria-autocomplete="list"
          aria-expanded={true}
          aria-controls={listId}
          aria-activedescendant={highlighted === undefined ? undefined : optionId(highlighted)}
          aria-describedby={describedBy}
          value={search}
          onChange={(event) => {
            setSearch(event.target.value);
            setActive(0);
          }}
          onKeyDown={onKeyDown}
        />
      )}
      {rows.status === 'loading' && (
        <span className="ay-selector__note" aria-busy="true">
          Chargement…
        </span>
      )}
      {rows.status === 'error' && <span className="ay-field__error">{rows.error.message}</span>}
      {truncated && !remote && <span className="ay-selector__note">Liste longue : les {SELECTOR_LIMIT} premiers objets ; la recherche interroge le serveur.</span>}
      {remote && answered === undefined && (
        <span className="ay-selector__note" aria-busy="true">
          Recherche…
        </span>
      )}
      {answered?.error !== undefined && <span className="ay-field__error">{answered.error}</span>}
      {rows.status === 'ready' && (!remote || answered?.rows !== undefined) && matches.length === 0 && <span className="ay-selector__note">Aucun résultat.</span>}
      {matches.length > 0 && (
        <ul
          id={searchable ? listId : id}
          role="listbox"
          className="ay-selector__list"
          aria-label={label}
          tabIndex={searchable ? -1 : 0}
          aria-activedescendant={!searchable && highlighted !== undefined ? optionId(highlighted) : undefined}
          onKeyDown={searchable ? undefined : onKeyDown}
        >
          {matches.map((row, index) => (
            <li
              key={String(row.id)}
              id={optionId(index)}
              role="option"
              className="ay-selector__option"
              aria-selected={index === highlighted}
              data-chosen={String(row.id) === current ? 'true' : undefined}
              data-testid={`selector-${String(row.id)}`}
              aria-label={selectorLabel(row, selector)}
              onMouseDown={(event) => event.preventDefault()}
              onClick={() => choose(row)}
            >
              {String(row.id) === current && (
                <span className="ay-selector__check" aria-hidden="true">
                  ✓
                </span>
              )}
              <SelectorCardBody card={selectorCard(row, selector)} />
            </li>
          ))}
        </ul>
      )}
      <button type="button" className="ay-button ay-button--secondary ay-selector__close" onClick={close}>
        Fermer
      </button>
    </div>
  );
}

const DETAIL_LABELS: Readonly<Record<SelectorCard['details'][number]['kind'], string>> = { address: 'Adresse', phone: 'Téléphone', email: 'Courriel' };

/** Avatar (initiales), nom en gras et sous-titre « N° numéro · commune » d'une carte de sélecteur. */
function SelectorCardBody({ card, large = false }: { card: SelectorCard; large?: boolean }) {
  return (
    <span className={`ay-selector__card${large ? ' ay-selector__card--large' : ''}`}>
      <span className="ay-selector__avatar" aria-hidden="true">
        {card.initials}
      </span>
      <span className="ay-selector__names">
        <span className="ay-selector__title">{card.title}</span>
        {card.subtitle !== '' && <span className="ay-selector__subtitle">{card.subtitle}</span>}
      </span>
    </span>
  );
}

/**
 * Registre des widgets rendus en propre sur le web. `keypad` : sur une chaîne, des chiffres au pavé
 * du poste ; sur un nombre, le rendu numérique du type (le clavier physique tient lieu de pavé) ;
 * `calc` est un champ calculé ; une date se tape JJ/MM/AAAA ({@link PadDateInput}) ; curseur, étoiles,
 * interrupteur, compteur, date et heure, texte riche assaini : `./widgets` (ADR-146 §6.1, T2) ; les
 * autres (photo, signature, scan…) retombent sur le rendu du type, les preuves restant portées par `evidence`.
 */
const WIDGETS: Partial<Record<Widget, (props: WidgetProps) => ReactNode>> = {
  radio_tiles: RadioTiles,
  time: TimeInput,
  lot: LotField,
  scan_code: ScanCode,
  toggle: Toggle,
  stepper: Stepper,
  keypad: KeypadDigits,
  slider: Slider,
  stars: Stars,
  datetime: DateTimeInput,
  html: RichTextBlock,
};

/**
 * Bornes d'un widget numérique, comme la tablette (`FieldWidgetProps`) : la cible résolue (règle partagée
 * servie par le serveur ou bornes écrites) et les bornes dures `validation.min` / `max`, séparément ;
 * l'unité de la cible, sinon celle du champ.
 */
function numericOf(entry: Entry, values: Answers, inputs: Inputs, rules: ConformityRules): Pick<WidgetProps, 'target' | 'validation' | 'unit'> {
  const resolved = resolveTarget(entry, values, inputs, rules);
  const rulesOfField = entry.field.validation;
  const target: Bounds = {};
  if (resolved?.min !== undefined) target.min = resolved.min;
  if (resolved?.max !== undefined) target.max = resolved.max;
  const validation: Bounds = {};
  if (typeof rulesOfField?.min === 'number') validation.min = rulesOfField.min;
  if (typeof rulesOfField?.max === 'number') validation.max = rulesOfField.max;
  const unit = resolved?.unit ?? entry.field.unit;
  return { target, validation, ...(unit === undefined || unit === '' ? {} : { unit: unitSymbol(unit) }) };
}

const STATE_LABEL: Readonly<Record<TargetState, string>> = {
  in: 'Dans la cible',
  out: 'Hors cible',
  wait: 'Pas encore dans la cible',
  nonconform: 'Non conforme',
  unknown: 'Vérifiée à la validation',
  none: '',
};

/** Ligne sous le champ : la cible, et l'état de la réponse pendant la saisie. */
function TargetLine({ id, entry, value, values, inputs }: { id: string; entry: Entry; value: unknown; values: Answers; inputs: Inputs }) {
  const rules = useContext(ConformityRulesContext);
  const target = resolveTarget(entry, values, inputs, rules);
  const state = targetState(entry, value, values, inputs, rules);
  if (target === undefined && state === 'none') return null;
  const tone = state === 'out' || state === 'nonconform' ? 'danger' : state === 'wait' ? 'warning' : state === 'in' ? 'success' : 'neutral';
  return (
    <span id={id} className={`ay-target ay-target--${tone}`} data-state={state}>
      {target !== undefined && <span className="ay-target__range">{formatTarget(target)}</span>}
      {state !== 'none' && <span className="ay-target__state">{STATE_LABEL[state]}</span>}
    </span>
  );
}

/**
 * Marque d'une réponse posée par une réaction « Fixer une valeur » et pas encore modifiée par l'opérateur
 * (ADR-144 §3.2, ADR-146 addendum (21)) : une suggestion se distingue d'une saisie.
 */
export function SuggestedMark({ controlId }: { controlId: string }) {
  return (
    <span className="ay-field__suggested" data-testid={`suggested-${controlId}`} title="Valeur proposée par la fiche : modifiez-la si elle ne convient pas.">
      proposée
    </span>
  );
}

export function TemplateEntry({
  entry,
  values,
  inputs,
  controlId,
  required,
  onChange,
  disabled,
  error,
  warnings,
  subject,
  onFill,
  path,
  children,
}: {
  entry: Entry;
  /** Réponses du groupe, champs calculés compris (portée `$answers` des bornes). */
  values: Answers;
  inputs: Inputs;
  /** Identifiant unique du champ dans la page (le groupe le préfixe). */
  controlId: string;
  required: boolean;
  onChange: (value: unknown) => void;
  disabled: boolean;
  error?: string;
  /** Avertissements de la réponse (doublon, motif manquant ; inspection-template-body 1.5.0) : visibles, jamais bloquants. */
  warnings?: readonly string[];
  subject?: { type: string; id: string };
  /** Champs du même groupe remplis par un scan (`presentation.scan.fills`, 1.9.0). */
  onFill?: (values: Readonly<Record<string, unknown>>) => void;
  /** Ce qui suit le champ dans son bloc (preuves exigées). */
  children?: ReactNode;
  /** Chemin de la réponse (`answers.x`, `answers.s.0.x`) : la marque « proposée » d'une réponse suggérée par une réaction. */
  path?: string;
}) {
  const id = entry.field.field_id;
  const value = values[id] ?? null;
  const targetId = `target-${controlId}`;
  const label = text(entry.field.label, 'fr', id);
  const today = useContext(CivilTodayContext);
  const rules = useContext(ConformityRulesContext);
  const workItemId = useContext(FormWorkItemContext);
  const widget = entry.presentation?.widget;
  const span = entry.presentation?.layout?.span === 'full' ? ' ay-form__entry--full' : '';
  const suggested = useSuggested(path);
  const hint = (
    <>
      {suggested && <SuggestedMark controlId={controlId} />}
      <TargetLine id={targetId} entry={entry} value={value} values={values} inputs={inputs} />
      {(warnings ?? []).map((message) => (
        <span key={message} className="ay-field__warning" role="status">
          {message}
        </span>
      ))}
    </>
  );

  if (entry.compute !== undefined || widget === 'calc') {
    return (
      <div className={`ay-form__entry ay-form__entry--computed${span}`}>
        <div className="ay-field">
          <span className="ay-field__label" id={`input-${controlId}-label`}>
            {label}
          </span>
          <output id={`input-${controlId}`} className="ay-computed" aria-labelledby={`input-${controlId}-label`} aria-describedby={targetId}>
            {value === null ? '—' : formatValue(entry, value)}
          </output>
          <span className="ay-computed__note">Calculé — le serveur recalcule à la validation</span>
          {hint}
          {error !== undefined && <span className="ay-field__error">{error}</span>}
        </div>
        {children}
      </div>
    );
  }

  const field: InputField = { ...inputFieldOf(entry.field, required), field_id: controlId };
  const render = widget === undefined ? undefined : WIDGETS[widget];
  const errorId = error === undefined ? undefined : `error-${controlId}`;
  const nonconformWhen = (entry as { nonconformity_when?: unknown }).nonconformity_when;
  const dangerOf = nonconformWhen === undefined ? undefined : (option: string): boolean => evaluateFormCondition(nonconformWhen as Parameters<typeof evaluateFormCondition>[0], { ...values, [id]: option });
  const presentation = entry.presentation as { step?: number; option_icons?: Record<string, string>; content?: unknown; scan?: WidgetProps['scan'] } | undefined;
  const props: WidgetProps = {
    id: `input-${controlId}`,
    field,
    definition: entry.field,
    value,
    onChange,
    disabled,
    invalid: error !== undefined,
    describedBy: [targetId, errorId].filter(Boolean).join(' ') || undefined,
    ...numericOf(entry, values, inputs, rules),
    today,
    dangerOf,
    step: presentation?.step,
    optionIcons: presentation?.option_icons,
    content: presentation?.content,
    scan: presentation?.scan,
    onFill,
    workItemId,
  };
  // Le widget déclaré d'abord ; puis, comme la tablette, le sélecteur d'objet d'une référence (une
  // preuve garde son téléversement) et la date civile ; sinon le rendu du type.
  // 1.10.0 : choix segmenté, tuiles à visuels (image, image de l'objet, pastille), réglages des tuiles.
  const choice = field.type === 'enumeration' || field.type === 'boolean';
  const tilesV2 = widget === 'radio_tiles' && entry.presentation !== undefined && (entry.presentation.option_visual !== undefined || entry.presentation.options !== undefined || entry.presentation.tiles !== undefined);
  let control =
    widget === 'segmented' && choice ? (
      <Segmented id={props.id} entry={entry} value={value} onChange={onChange} disabled={disabled} required={required} labelledBy={`input-${controlId}-label`} />
    ) : tilesV2 && choice ? (
      <OptionTiles id={props.id} entry={entry} value={value} onChange={onChange} disabled={disabled} required={required} dangerOf={dangerOf} labelledBy={`input-${controlId}-label`} />
    ) : (
      render?.(props)
    );
  // Champ « Personne » (ADR-152) : puce, champ ou bouton, et la grille des personnes de la source du gabarit.
  if (widget === 'person' && field.type === 'reference') {
    control = <PersonControl id={props.id} entry={entry} value={value} required={required} disabled={disabled} invalid={error !== undefined} onChange={onChange} labelledBy={`input-${controlId}-label`} />;
  }
  if ((control === undefined || control === null) && field.type === 'reference' && field.reference_type !== undefined && field.reference_type !== 'Evidence') {
    control = <ObjectSelector {...props} type={field.reference_type} selector={selectorOf(entry)} label={label} required={required} />;
  }
  if ((control === undefined || control === null) && field.type === 'date' && widget !== 'datetime') {
    // Dates rapides déclarées (inspection-template-body 1.7.0) : sous le champ.
    const quick = (entry.presentation as { quick_dates?: readonly string[] } | undefined)?.quick_dates ?? [];
    control = <PadDateInput {...props} required={required} today={today} quick={quick} />;
  }
  if ((control === undefined || control === null) && field.type === 'datetime') {
    // Un instant se tape à l'heure du site, jamais à celle du poste (R1) : même rendu sans widget déclaré.
    control = <DateTimeInput {...props} />;
  }
  if (control === undefined || control === null) {
    const fallback: Parameters<typeof FieldInput>[0] = { field, value, onChange, disabled };
    if (error !== undefined) fallback.error = error;
    if (subject !== undefined) fallback.subject = subject;
    return (
      <div className={`ay-form__entry${span}`}>
        <FieldInput {...fallback} />
        {hint}
        {children}
      </div>
    );
  }
  return (
    <div className={`ay-form__entry${span}`}>
      <div className="ay-field">
        <label className="ay-field__label" id={`input-${controlId}-label`} htmlFor={`input-${controlId}`}>
          {label}
          {required && <span aria-hidden="true"> *</span>}
        </label>
        <EchoName target={id} />
        {control}
        {hint}
        {error !== undefined && (
          <span id={errorId} className="ay-field__error">
            {error}
          </span>
        )}
      </div>
      {children}
    </div>
  );
}
